import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";
import FaqAccordion from "@/pages/guide/components/FaqAccordion";
import ReservationForm from "@/pages/guide/components/ReservationForm";

const notices = [
  { icon: "ri-time-line", title: "提前到场", desc: "放映前 20 分钟开始检票，建议提前 15 分钟入场熟悉穹顶环境。" },
  { icon: "ri-forbid-2-line", title: "禁止拍摄", desc: "放映过程中请勿拍照、录像或使用闪光灯，共同维护观影氛围。" },
  { icon: "ri-emotion-sad-line", title: "晕动提示", desc: "若对高速运动镜头敏感，建议选择中后排座位，不适时可随时离场休息。" },
  { icon: "ri-parent-line", title: "适龄观影", desc: "亲子场次欢迎家庭共同入场，强刺激影片建议 8 岁以上观众观看。" },
  { icon: "ri-suitcase-line", title: "物品保管", desc: "大件行李请交由前台寄存，贵重物品请随身携带并妥善保管。" },
  { icon: "ri-wheelchair-line", title: "无障碍服务", desc: "场馆提供无障碍坡道、专用座位与轮椅借用，需协助可联系工作人员。" },
];

const steps = [
  { step: "01", title: "在线选片", desc: "在影片分类页浏览并选择你想看的穹顶影片。" },
  { step: "02", title: "选择场次", desc: "在放映动态页挑选合适的日期、时间与放映厅。" },
  { step: "03", title: "确认预约", desc: "填写观影意向，我们会为你确认座位与取票方式。" },
  { step: "04", title: "到店入场", desc: "凭预约信息到前台取票，检票后即可入场体验。" },
];

const services = [
  { icon: "ri-luggage-cart-line", label: "免费自助寄存" },
  { icon: "ri-parking-box-line", label: "地下停车场" },
  { icon: "ri-cup-line", label: "观影休息区" },
  { icon: "ri-store-3-line", label: "电影文创商店" },
  { icon: "ri-parent-line", label: "母婴室" },
  { icon: "ri-wifi-line", label: "全场免费 Wi-Fi" },
];

export default function Guide() {
  usePageMeta(
    "观影指南-球幕影院观影须知与购票流程",
    "球幕影院观影指南：了解观影须知、预约购票流程、场馆服务与常见问题解答，让首次穹顶观影更从容。",
    "球幕影院观影指南,观影须知,购票流程,常见问题,场馆服务",
  );
  return (
    <div className="w-full pt-20">
      <section className="bg-background-100/60 border-b border-background-200/60">
        <div className="max-w-content mx-auto px-4 md:px-8 py-12 md:py-16">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">观影指南</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 mb-4">观影指南</h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            第一次来球幕影院？这里有你需要了解的一切：观影须知、购票流程、场馆服务与常见问题解答。
          </p>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-8 py-14 md:py-16">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-8">观影须知</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {notices.map((item, i) => (
            <Reveal key={item.title} delay={i * 70}>
              <div className="h-full p-5 rounded-lg bg-background-100 border border-background-200/70">
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-100/90 text-secondary-900 text-xl mb-4">
                  <i className={item.icon} />
                </span>
                <h3 className="font-heading font-semibold text-foreground-950 mb-1.5">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-background-100/60 border-y border-background-200/60">
        <div className="max-w-content mx-auto px-4 md:px-8 py-14 md:py-16">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-8">预约购票流程</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((item, i) => (
              <Reveal key={item.step} delay={i * 80}>
                <div className="relative h-full p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <span className="font-heading font-bold text-3xl text-accent-500/40">{item.step}</span>
                  <h3 className="font-heading font-semibold text-foreground-950 mt-3 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-8 py-14 md:py-16">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-8">场馆服务</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70"
            >
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500/15 text-primary-700 text-lg shrink-0">
                <i className={item.icon} />
              </span>
              <span className="text-sm text-foreground-800">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100/60 border-t border-background-200/60">
        <div className="max-w-content mx-auto px-4 md:px-8 py-14 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2">
                常见问题 FAQ
              </h2>
              <p className="text-sm text-foreground-600 mb-6">点击问题即可展开查看解答</p>
              <FaqAccordion />
            </div>
            <div>
              <ReservationForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}